import { Link } from "react-router-dom";
import { Send, Twitter, Linkedin, Github } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const links = {
    company: [
      { label: "Home", to: "/" },
      { label: "About Us", to: "/about" },
      { label: "Services", to: "/services" },
      { label: "Projects", to: "/projects" },
    ],
    resources: [
      { label: "News & Articles", to: "/news" },
      { label: "Internships", to: "/internships" },
      { label: "Contact Us", to: "/contact" },
      { label: "Terms of Service", to: "/" },
    ],
    social: [
      { icon: Twitter, href: "#", label: "Twitter" },
      { icon: Linkedin, href: "#", label: "LinkedIn" },
      { icon: Github, href: "#", label: "GitHub" },
    ],
  };

  return (
    <footer
      className="relative z-10 border-t border-white/5 pt-20 pb-10 overflow-hidden"
      style={{ background: "#060B1F" }}
    >
      {/* Glow effect */}
      <div
        className="absolute bottom-[-150px] right-[5%] w-[450px] h-[450px] rounded-full opacity-[0.08] blur-[130px] pointer-events-none"
        style={{
          background: "radial-gradient(circle, #7C4DFF, transparent 70%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 pb-16">
          {/* Logo & Branding */}
          <div className="md:col-span-4 flex flex-col gap-6">
            <Link to="/" className="flex items-center gap-3 no-underline group">
              <div className="relative">
                <div className="w-10 h-10 rounded-xl overflow-hidden bg-slate-950 flex items-center justify-center border border-white/10 shadow-lg group-hover:border-[#7C4DFF]/50 transition-colors duration-300">
                  <img src="/cc.png" alt="ClientCode logo" className="w-full h-full object-cover" />
                </div>
                <div
                  className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#7C4DFF] to-[#27B8FF] blur-md opacity-20 group-hover:opacity-40 transition-opacity"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-md font-bold text-white tracking-wider leading-tight">
                  CLIENTCODE AI
                </span>
                <span className="text-[9px] font-semibold tracking-[0.25em] text-[#27B8FF] uppercase">
                  Innovation Pvt. Ltd.
                </span>
              </div>
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed max-w-sm">
              We specialize in custom enterprise solutions, custom AI systems,
              web platforms, mobile apps, automation pipelines, and cloud native architectures.
            </p>
            {/* Social Icons */}
            <div className="flex gap-4">
              {links.social.map((item, i) => (
                <a
                  key={i}
                  href={item.href}
                  className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/5 border border-white/10 text-slate-400 transition-all duration-300 hover:text-[#27B8FF] hover:border-[#27B8FF]/40 hover:bg-[#27B8FF]/5"
                  aria-label={item.label}
                >
                  <item.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-2 col-span-6 flex flex-col gap-5">
            <h4 className="text-sm font-bold text-white uppercase tracking-widest">
              Company
            </h4>
            <ul className="flex flex-col gap-3.5 list-none p-0 m-0">
              {links.company.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="text-sm text-slate-400 no-underline transition-colors duration-300 hover:text-white"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources & Support */}
          <div className="md:col-span-2 col-span-6 flex flex-col gap-5">
            <h4 className="text-sm font-bold text-white uppercase tracking-widest">
              Resources
            </h4>
            <ul className="flex flex-col gap-3.5 list-none p-0 m-0">
              {links.resources.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="text-sm text-slate-400 no-underline transition-colors duration-300 hover:text-white"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter Box */}
          <div className="md:col-span-4 flex flex-col gap-5">
            <h4 className="text-sm font-bold text-white uppercase tracking-widest">
              Join Our Newsletter
            </h4>
            <p className="text-sm text-slate-400 leading-relaxed">
              Stay ahead with advanced AI insights, automation strategies, and release updates.
            </p>
            <div className="relative mt-2">
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full h-12 rounded-xl border border-white/10 bg-white/5 px-4 pr-12 text-sm text-white placeholder:text-slate-500 focus:border-[#7C4DFF]/40 focus:outline-none"
              />
              <button
                type="button"
                className="absolute right-1 top-1 w-10 h-10 rounded-lg flex items-center justify-center text-slate-950 transition-all duration-300 hover:scale-105"
                style={{
                  background: "linear-gradient(135deg, #7C4DFF, #27B8FF)",
                }}
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="border-t border-white/5 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-500">
          <p>© {currentYear} CLIENTCODE AI Innovation Pvt. Ltd. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="#!" className="no-underline transition-colors hover:text-white">Privacy Policy</a>
            <a href="#!" className="no-underline transition-colors hover:text-white">Terms of Use</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
